import { Pages, routeBuilders, routeUpdaters } from './routes/routesState'
import {
  CmsPageState, PageWithBlocks,
} from './cmsPage/cmsPageState'
import { FooterState, initialFooterState } from './footer/footerState'
import { NotFoundState } from './notFound/notFoundState'
import { CookiebarState, initialCookiebarState } from './cookiebar/cookiebarState'
import { HeaderState, initialHeaderState } from './header/headerState'
import { initialViewListenerState, ViewListenerState } from './viewListener/viewListenerState'
import { SearchState } from './search/searchState'
import { CourseSearchState } from './courseSearch/courseSearchState'
import { CourseDateScheduleState } from './courseDateSchedule/courseDateScheduleState'
import { EducationFormState } from './educationForm/educationFormState'
import { MailPlusFormState } from './mailPlusForm/mailPlusFormState'
import { initialJqueryState, JqueryState } from './jquery/jqueryState'
import { initialRedirectState, RedirectState } from './redirects/redirectState'
import { EmptyState } from './empty/emptyState'
import { KnowledgeSearchState } from './knowledgeSearch/knowledgeSearchState'
import { ActualitySearchState } from './actualitySearch/actualitySearchState'
import { Updater, initialRoute } from 'widgets-for-react'
import { CurrentPage } from '../utils/routes'
import { routesArray } from './routes/routesWidget'
import { MagazinesOverviewPageState } from './magazinesOverview/magazinesOverviewState'
import { EventFormState } from './eventForm/eventForm.state'
import { OpleidingenOverviewPageState } from './opleidingenOverview/opleidingenOverviewState'


export type RootProps = {
  footerFormId: string
  useSSR: string
}

export type RootState = {
  view: 'mobile' | 'desktop'
  page: CurrentPage<Pages>
  cookiebarState: CookiebarState
  headerState: HeaderState
  footerState: FooterState
  jquery: JqueryState
  redirect: RedirectState
  firstTimeLoading: boolean
}

export const initialRootState: (
  _: RootProps
) => RootState = props => {

  const initialState : RootState = {
    view: initialViewListenerState,
    page: routeBuilders.empty.make({}),
    cookiebarState: initialCookiebarState(),
    headerState: initialHeaderState,
    footerState: initialFooterState(props.footerFormId),
    jquery: initialJqueryState(),
    redirect: initialRedirectState(),
    firstTimeLoading: true
  }

  const updater = initialRoute(routesArray)
  return updater === undefined ? initialState : updater(initialState)
}

export const rootStateUpdaters = {
  updateViewListenerState:
    (update: ViewListenerState) =>
    (state: RootState): RootState => ({
      ...state,
      view: update,
    }),

  updateCookiebarState:
    (updater: Updater<CookiebarState>) =>
    (state: RootState): RootState => ({
      ...state,
      cookiebarState: updater(state.cookiebarState),
    }),
  updateRedirectState:
    (updater: Updater<RedirectState>) =>
    (state: RootState): RootState => ({
      ...state,
      redirect: updater(state.redirect),
    }),

  updateJqueryState:
    (updater: Updater<JqueryState>) =>
    (state: RootState): RootState => ({
      ...state,
      jquery: updater(state.jquery),
    }),

  updateFirstTimeLoadingState:
    (updater: Updater<RootState>) =>
    (state: RootState): RootState => {
      const newState = updater(state)
      let firstTime = true

      switch (state.page.kind) {
        case "cmsPage":
        case "previewPage":
        case "home":
          if (state.page.pageState.pageWithBlocksState.kind !== "loading") firstTime = false;
        default:
          firstTime = false;
      }

      return ({
      ...newState,
      firstTimeLoading: firstTime
    })},

  updateHeaderState:
    (updater: Updater<HeaderState>) =>
    (state: RootState): RootState => ({
      ...state,
      headerState: updater(state.headerState),
    }),

  updateFooterState:
    (updater: Updater<FooterState>) =>
    (state: RootState): RootState => ({
      ...state,
      footerState: updater(state.footerState),
    }),

  updateSearchPageState:
    (updater: Updater<SearchState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'search'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateOpleidingenOverviewPageState:
    (updater: Updater<OpleidingenOverviewPageState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'opleidingenOverview'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateMagazinesOverviewPageState:
    (updater: Updater<MagazinesOverviewPageState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'magazinesOverviewPage'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateCourseSearchPageState:
    (updater: Updater<CourseSearchState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'courseSearch'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
    updateActualitySearchPageState:
    (updater: Updater<ActualitySearchState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'actualitySearch'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
    updateKnowledgeSearchPageState:
    (updater: Updater<KnowledgeSearchState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'knowledgeSearch'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateCourseDateSchedulePageState:
    (updater: Updater<CourseDateScheduleState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'courseDateSchedule'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateEducationFormPageState:
    (updater: Updater<EducationFormState>) =>
    (state: RootState): RootState => ({...state,
      page:
        state.page.kind === 'educationForm'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateEventFormPageState:
    (updater: Updater<EventFormState>) =>
    (state: RootState): RootState => ({...state,
      page:
        state.page.kind === 'eventForm'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),
  updateMailPlusFormPageState:
    (updater: Updater<MailPlusFormState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'mailPlusForm'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateNotFoundPageState:
    (updater: Updater<NotFoundState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'notFound'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateEmptyState:
    (updater: Updater<EmptyState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'empty'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateHomePageState:
    (updater: Updater<CmsPageState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'home'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updatePreviewPageState:
    (updater: Updater<CmsPageState>) =>
    (state: RootState): RootState => ({
      ...state,
      page:
        state.page.kind === 'previewPage'
          ? {
              ...state.page,
              pageState: updater(state.page.pageState),
            }
          : state.page,
    }),

  updateCmsPageState: (updater: Updater<CmsPageState>) => (
    state: RootState
  ): RootState => {
    if (state.page.kind !== 'cmsPage') {
      return state
    }
    const newState = updater(state.page.pageState)

    return {
      ...state,
      page: {
        ...state.page,
        pageState:
          newState.pageWithBlocksState.kind === 'loaded' &&
          state.page.pageState.pageWithBlocksState.kind !== 'loaded' &&
          newState.pageWithBlocksState.value.path !== window.location.pathname
            ? {
                ...state.page.pageState,
                pageWithBlocksState: {
                  kind: 'redirect',
                  to: GetRedirectLink(newState.pageWithBlocksState.value)
                }
              }
            : newState
      }
    }
  },
}


const GetRedirectLink = (page: PageWithBlocks) => {
  const params = new URLSearchParams(window.location.search)
  if (params.get('preview')) {
    params.append('from', window.location.pathname?.replace(/^\//, '') ?? '')
  }

  return `${page.path}?${params.toString()}`
}
